import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Stack, Group, Button, SegmentedControl, Slider, Text, Alert } from '@mantine/core';
import { api } from '../api/client';

type Aspect = '16:9' | '1:1';
type Entity = 'technicians' | 'equipment' | 'accessories' | 'consumables' | 'customers' | 'orders' | 'parts' | 'company';

// Output width for the cropped upload. 16:9 → 1280×720, 1:1 → 800×800.
const OUT_SIZE: Record<Aspect, { w: number; h: number }> = {
  '16:9': { w: 1280, h: 720 },
  '1:1':  { w: 800,  h: 800 },
};

export default function CropImageModal({
  src,
  entity,
  initialAspect = '1:1',
  onClose,
  onSaved,
}: {
  src: string;             // existing image src (e.g. '/images/foo/bar.jpg') OR a blob: URL from a fresh pick
  entity: Entity;
  initialAspect?: Aspect;
  onClose: () => void;
  onSaved: (path: string) => void;
}) {
  const [aspect, setAspect] = useState<Aspect>(initialAspect);
  const [zoom, setZoom] = useState(1);        // 1.0 = fit, 2.0 = 200% etc.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; offX: number; offY: number } | null>(null);

  const target = OUT_SIZE[aspect];
  const previewScale = 0.55; // shrink the preview canvas to fit the modal
  const previewW = Math.round(target.w * previewScale);
  const previewH = Math.round(target.h * previewScale);

  // Load the image.
  useEffect(() => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => setImg(el);
    el.onerror = () => setErr('Failed to load image. Try re-uploading.');
    el.src = src;
  }, [src]);

  // Recenter + reset zoom whenever the image or aspect changes.
  useEffect(() => {
    setOffset({ x: 0, y: 0 });
    setZoom(1);
  }, [img, aspect]);

  // Base scale = fit the image into the target frame at zoom=1 (cover, not contain, so the frame is fully painted).
  const baseScale = useMemo(() => {
    if (!img) return 1;
    return Math.max(target.w / img.width, target.h / img.height);
  }, [img, target]);

  // Draw the preview.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !img) return;
    canvas.width = previewW;
    canvas.height = previewH;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, previewW, previewH);

    const s = baseScale * zoom * previewScale;
    const dw = img.width * s;
    const dh = img.height * s;
    const cx = previewW / 2 + offset.x * previewScale;
    const cy = previewH / 2 + offset.y * previewScale;
    ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);

    // Frame outline.
    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, previewW - 1, previewH - 1);
  }, [img, zoom, offset, baseScale, previewW, previewH, previewScale]);

  function onMouseDown(e: React.MouseEvent) {
    dragRef.current = { startX: e.clientX, startY: e.clientY, offX: offset.x, offY: offset.y };
  }
  function onMouseMove(e: React.MouseEvent) {
    if (!dragRef.current) return;
    const dx = (e.clientX - dragRef.current.startX) / previewScale;
    const dy = (e.clientY - dragRef.current.startY) / previewScale;
    setOffset({ x: dragRef.current.offX + dx, y: dragRef.current.offY + dy });
  }
  function onMouseUp() { dragRef.current = null; }

  async function save() {
    if (!img) return;
    setErr(null);
    setBusy(true);
    try {
      // Render at full output size to a fresh canvas, matching the preview.
      const out = document.createElement('canvas');
      out.width = target.w;
      out.height = target.h;
      const ctx = out.getContext('2d')!;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, target.w, target.h);

      const s = baseScale * zoom;
      const dw = img.width * s;
      const dh = img.height * s;
      const cx = target.w / 2 + offset.x;
      const cy = target.h / 2 + offset.y;
      ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);

      const blob: Blob = await new Promise((res, rej) =>
        out.toBlob((b) => (b ? res(b) : rej(new Error('canvas export failed'))), 'image/jpeg', 0.9)
      );
      const file = new File([blob], 'crop.jpg', { type: 'image/jpeg' });
      const uploaded = await api.uploadImage(entity, file);
      onSaved(uploaded.path);
    } catch (e: any) {
      setErr(e.message ?? 'Crop failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened onClose={onClose} size="lg" title={<span className="modal-title-custom">Crop Image</span>}>
      <Stack>
        <Group justify="space-between">
          <SegmentedControl
            value={aspect}
            onChange={(v) => setAspect(v as Aspect)}
            data={[
              { value: '1:1', label: '1 : 1' },
              { value: '16:9', label: '16 : 9' },
            ]}
          />
          <Text size="xs" c="dimmed">Drag to pan · slider to zoom</Text>
        </Group>

        <div style={{ background: '#eee', padding: 12, display: 'flex', justifyContent: 'center' }}>
          <canvas
            ref={canvasRef}
            width={previewW}
            height={previewH}
            style={{ cursor: dragRef.current ? 'grabbing' : 'grab', userSelect: 'none', background: '#fff' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          />
        </div>

        <Stack gap={2}>
          <Text size="sm">Zoom: {(zoom * 100).toFixed(0)}%</Text>
          <Slider value={zoom} onChange={setZoom} min={1} max={4} step={0.05} label={(v) => `${(v * 100).toFixed(0)}%`} />
        </Stack>

        {err && <Alert color="red">{err}</Alert>}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={!img}>Save crop</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
